import React, { useMemo, useState } from 'react';
import { Form, Button, Alert } from 'react-bootstrap';
import { X } from 'lucide-react';
import { PhotoModel, BlueprintLocation, Building } from '../../../types/protocols';
import { currentDrawings } from '../../../Helpers/blueprintLookup';
import BlueprintWithMarkers from '../../../Components/BlueprintWithMarkers';

interface LocationMarkerPickerProps {
    building: Building;
    value?: {
        type: 'blueprint' | 'satellite';
        sourceKey: string;
        marker: BlueprintLocation;
    };
    onChange: (location: {
        type: 'blueprint' | 'satellite';
        sourceKey: string;
        marker: BlueprintLocation;
    } | undefined) => void;
}

interface SourceOption {
    key: string;
    type: 'blueprint' | 'satellite';
    label: string;
    /** The image key the marker would be stored under on a real blueprintLocations map. */
    imageKey: string;
    photo: PhotoModel;
}

const hasUsableSize = (photo: PhotoModel): boolean =>
    Number.isFinite(photo.width) && photo.width > 0 && Number.isFinite(photo.height) && photo.height > 0;

/**
 * LocationMarkerPicker - place one marker on a drawing or the satellite image
 * for a building check.
 *
 * A thin shell (plan §5.4): the source dropdown (every level's current drawing,
 * the roof drawing, the satellite image), the remove button and the stored
 * `{type, sourceKey, marker}` contract are its own; the picture, zoom, pan and
 * click-to-place come from BlueprintWithMarkers in placement mode, so drawings
 * with a vector source are sharp here too. No wheel zoom, by design (D16):
 * buttons and double-click, as everywhere else. The marker is a BlueprintLocation
 * with the source's node size as reference, for staleness detection.
 */
const LocationMarkerPicker: React.FC<LocationMarkerPickerProps> = ({
    building,
    value,
    onChange
}) => {
    const [selectedSourceKey, setSelectedSourceKey] = useState<string>(value?.sourceKey || '');

    // Build source options from building data
    const sourceOptions = useMemo((): SourceOption[] => {
        const options: SourceOption[] = [];

        // Add satellite/map images
        const mapImages = building.model?.mapImages || {};
        const currentMapImageKey = building.model?.currentMapImageKey;

        // Add current map image first if exists
        if (currentMapImageKey && mapImages[currentMapImageKey]) {
            options.push({
                key: `satellite_${currentMapImageKey}`,
                type: 'satellite',
                label: 'Satellitbild',
                imageKey: currentMapImageKey,
                photo: mapImages[currentMapImageKey]
            });
        }

        // Add the current drawing of every level (ascending) and the roof drawing (last)
        currentDrawings(building.model?.structure).forEach(drawing => {
            const ownerKey = drawing.owner.kind === 'level' ? drawing.owner.level.key : 'roofPlan';
            options.push({
                key: `blueprint_${ownerKey}_${drawing.imageKey}`,
                type: 'blueprint',
                label: `${drawing.label} - Ritning`,
                imageKey: drawing.imageKey,
                photo: drawing.image
            });
        });

        // Placement writes fractions of the node's width/height (plan §3.3); a
        // legacy image without a size would make every click a silent no-op.
        return options.filter(option => hasUsableSize(option.photo));
    }, [building]);

    // Get currently selected source
    const selectedSource = useMemo(() => {
        return sourceOptions.find(opt => opt.key === selectedSourceKey);
    }, [sourceOptions, selectedSourceKey]);

    // The marker shown is the stored value, and only while it belongs to the selected source.
    const currentMarker = value && value.sourceKey === selectedSourceKey ? value.marker : undefined;

    // Handle source selection change
    const handleSourceChange = (key: string) => {
        setSelectedSourceKey(key);
        // Clear marker when source changes
        onChange(undefined);
    };

    // Click on the picture: BlueprintWithMarkers reports the point in the source's
    // pixel space with the node's width/height as reference (plan §3.3).
    const handlePlace = (location: BlueprintLocation) => {
        if (!selectedSource) return;
        onChange({
            type: selectedSource.type,
            sourceKey: selectedSource.key,
            marker: location
        });
    };

    // Handle marker removal
    const handleRemoveMarker = () => {
        onChange(undefined);
    };

    if (sourceOptions.length === 0) {
        return (
            <Alert variant="info">
                Inga ritningar eller satellitbilder tillgängliga för denna byggnad.
                Lägg till ritningar på plansidor eller en satellitbild på byggnadssidan.
            </Alert>
        );
    }

    return (
        <div className="location-marker-picker">
            {/* Source Selector */}
            <Form.Group className="mb-3">
                <Form.Label>Välj bild att markera på</Form.Label>
                <Form.Select
                    value={selectedSourceKey}
                    onChange={(e) => handleSourceChange(e.target.value)}
                >
                    <option value="">Välj ritning eller satellitbild...</option>
                    {sourceOptions.map(option => (
                        <option key={option.key} value={option.key}>
                            {option.label}
                        </option>
                    ))}
                </Form.Select>
            </Form.Group>

            {/* Picture with the marker */}
            {selectedSource && (
                <div className="mb-3">
                    <small className="text-muted d-block mb-2">
                        Klicka på bilden för att placera markör • Dra för att panorera, dubbelklicka för att zooma
                    </small>

                    <BlueprintWithMarkers
                        key={selectedSource.key}
                        blueprint={selectedSource.photo}
                        currentBlueprintKey={selectedSource.imageKey}
                        building={building}
                        hideTitle
                        placementMode
                        onBlueprintClick={handlePlace}
                        pointMarker={currentMarker}
                    />

                    {/* Remove Marker Button */}
                    {currentMarker && (
                        <div className="mt-2">
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={handleRemoveMarker}
                            >
                                <X size={14} className="me-1" />
                                Ta bort markering
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LocationMarkerPicker;
